// React Query hooks do painel Super Master (Empresas) — módulo separado de
// hooks/queries/index.ts de propósito: aquele arquivo é 100% escopado à
// sessão de empresa (gpsdrive-client.ts); estas queries usam o cliente do
// painel Super Master (gpsdrive-platform-client.ts), sessão inteiramente
// diferente.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  gpsDriveCompaniesService,
  type CreateCompanyInput,
  type UpdateCompanyInput,
} from "@/services/gpsdrive-companies.service";
import { gpsDriveNotificationSoundsService } from "@/services/gpsdrive-notification-sounds.service";
import { gpsDriveAppReleasesService } from "@/services/gpsdrive-app-releases.service";
import {
  gpsDriveSmsCreditsService,
  type RechargeSmsAccountInput,
} from "@/services/gpsdrive-sms-credits.service";
import {
  gpsDriveTrackerProtocolsService,
  type CreateTrackerProtocolInput,
} from "@/services/gpsdrive-tracker-protocols.service";
import {
  gpsDrivePlatformIntegrationsService,
  type UpdateUazapiSettingsInput,
  type UpdateFaceVerificationSettingsInput,
  type SecurityWhatsappGroup,
} from "@/services/gpsdrive-platform-integrations.service";
import { jt808CameraService } from "@/services/jt808-camera.service";
import { gpsDrivePlatformMigrationService } from "@/services/gpsdrive-platform-migration.service";

const companiesKey = ["platform", "companies"] as const;
export const notificationSoundsKey = ["notification-sounds"] as const;
const smsAccountsKey = ["platform", "sms-credits"] as const;
// Sem prefixo "platform" de propósito — mesma chave/service usada pelo
// cadastro de veículo e catálogo de Comandos GT06 (empresa comum, sem
// sessão de plataforma), mesmo espírito de notificationSoundsKey acima.
export const trackerProtocolsKey = ["tracker-protocols"] as const;
// Mesmo espírito de notificationSoundsKey acima — GET é público (ver
// gpsdrive-app-releases.service.ts), sem prefixo "platform".
export const appReleasesKey = ["app-releases"] as const;

export function useCompanies(enabled = true) {
  return useQuery({
    queryKey: companiesKey,
    queryFn: () => gpsDriveCompaniesService.list(),
    enabled,
    retry: false,
  });
}

export function useCreateCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCompanyInput) => gpsDriveCompaniesService.create(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: companiesKey }),
  });
}

export function useUpdateCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateCompanyInput }) =>
      gpsDriveCompaniesService.update(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: companiesKey }),
  });
}

// Sons de notificação (tela Super Master > Notificações) — query key sem
// prefixo "platform" de propósito: a mesma chave/service é usada pelo
// player de som em tempo real do app de empresa (ver
// use-notification-sound-player.ts), que não tem sessão de plataforma.
export function useNotificationSounds() {
  return useQuery({
    queryKey: notificationSoundsKey,
    queryFn: () => gpsDriveNotificationSoundsService.list(),
    retry: false,
  });
}

// Fotos de alarme da câmera JT808 (painel Super Master > ambiente da
// câmera) — ver jt808-camera.service.ts. Sem staleTime: a lista pode
// crescer a qualquer momento (novo alarme real da WY02), então prefere
// refetch a mostrar uma lista velha.
export function useJt808CameraPhotos(imei: string) {
  return useQuery({
    queryKey: ["jt808-camera", imei, "photos"],
    queryFn: () => jt808CameraService.listPhotos(imei),
    retry: false,
  });
}

export function useUploadNotificationSound() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ alertKey, file }: { alertKey: string; file: File }) =>
      gpsDriveNotificationSoundsService.upload(alertKey, file),
    onSuccess: () => qc.invalidateQueries({ queryKey: notificationSoundsKey }),
  });
}

export function useDeleteNotificationSound() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (alertKey: string) => gpsDriveNotificationSoundsService.remove(alertKey),
    onSuccess: () => qc.invalidateQueries({ queryKey: notificationSoundsKey }),
  });
}

// Seletor do botão "Enviar teste" — frota inteira (todas as empresas),
// staleTime alto de propósito: é só pra achar o veículo pelo nome, não
// precisa refletir status online/offline em tempo real.
export function useTestPushDevices() {
  return useQuery({
    queryKey: ["notification-sounds", "test-push-devices"],
    queryFn: () => gpsDriveNotificationSoundsService.listTestPushDevices(),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

// Botão "Enviar teste" (tela Super Master > Notificações, 2026-09-14) —
// sem invalidação de cache, é uma ação fire-and-forget, não muda nenhum
// dado listado na tela.
export function useSendTestPush() {
  return useMutation({
    mutationFn: (deviceId: number) => gpsDriveNotificationSoundsService.sendTestPush(deviceId),
  });
}

// Histórico de versões do APK (tela Super Master > Aplicativo).
export function useAppReleases() {
  return useQuery({
    queryKey: appReleasesKey,
    queryFn: () => gpsDriveAppReleasesService.list(),
    retry: false,
  });
}

export function useUploadAppRelease() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ version, file }: { version: string; file: File }) =>
      gpsDriveAppReleasesService.upload(version, file),
    onSuccess: () => qc.invalidateQueries({ queryKey: appReleasesKey }),
  });
}

export function useDeleteAppRelease() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => gpsDriveAppReleasesService.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: appReleasesKey }),
  });
}

// Saldo/ativação de SMS por empresa (tela Super Master > SMS).
export function useSmsAccounts() {
  return useQuery({
    queryKey: smsAccountsKey,
    queryFn: () => gpsDriveSmsCreditsService.list(),
    retry: false,
  });
}

export function useToggleSmsAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ companyId, enabled }: { companyId: string; enabled: boolean }) =>
      gpsDriveSmsCreditsService.toggle(companyId, enabled),
    onSuccess: () => qc.invalidateQueries({ queryKey: smsAccountsKey }),
  });
}

export function useRechargeSmsAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ companyId, input }: { companyId: string; input: RechargeSmsAccountInput }) =>
      gpsDriveSmsCreditsService.recharge(companyId, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: smsAccountsKey }),
  });
}

// Catálogo de protocolos de rastreador (tela Super Master > Protocolos) —
// também consumido pelo cadastro de veículo e pelo catálogo de Comandos
// GT06 (empresa comum), ver comentário de trackerProtocolsKey acima.
export function useTrackerProtocols() {
  return useQuery({
    queryKey: trackerProtocolsKey,
    queryFn: () => gpsDriveTrackerProtocolsService.list(),
    staleTime: 60_000,
    retry: false,
  });
}

export function useCreateTrackerProtocol() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTrackerProtocolInput) => gpsDriveTrackerProtocolsService.create(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: trackerProtocolsKey }),
  });
}

export function useUpdateTrackerProtocolLabel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, label }: { id: string; label: string }) =>
      gpsDriveTrackerProtocolsService.updateLabel(id, label),
    onSuccess: () => qc.invalidateQueries({ queryKey: trackerProtocolsKey }),
  });
}

export function useDeleteTrackerProtocol() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => gpsDriveTrackerProtocolsService.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: trackerProtocolsKey }),
  });
}

// Painel Super Master > Integrações (pedido 2026-09-08) — token/URL admin
// da uazapi + dashboard de comportamento do WhatsApp.
const uazapiSettingsKey = ["platform", "integrations", "uazapi"] as const;
const whatsappDashboardKey = (days: number) => ["platform", "integrations", "whatsapp-dashboard", days] as const;

export function useUazapiSettings() {
  return useQuery({
    queryKey: uazapiSettingsKey,
    queryFn: () => gpsDrivePlatformIntegrationsService.getUazapiSettings(),
    retry: false,
  });
}

export function useUpdateUazapiSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateUazapiSettingsInput) => gpsDrivePlatformIntegrationsService.updateUazapiSettings(input),
    onSuccess: (data) => qc.setQueryData(uazapiSettingsKey, data),
  });
}

// `refetchInterval` mantém o painel vivo sem precisar de F5 enquanto o
// Super Master está de olho monitorando um problema — custo baixo (uma
// consulta agregada, sem N+1).
export function useWhatsappIntegrationsDashboard(days = 30) {
  return useQuery({
    queryKey: whatsappDashboardKey(days),
    queryFn: () => gpsDrivePlatformIntegrationsService.getWhatsappDashboard(days),
    refetchInterval: 30_000,
    retry: false,
  });
}

// Aba "OpenAI" (pedido 2026-09-11) — verificação de rosto na assinatura de
// contrato, configuração única de plataforma.
const faceVerificationSettingsKey = ["platform", "integrations", "face-verification"] as const;

export function useFaceVerificationSettings() {
  return useQuery({
    queryKey: faceVerificationSettingsKey,
    queryFn: () => gpsDrivePlatformIntegrationsService.getFaceVerificationSettings(),
    retry: false,
  });
}

export function useUpdateFaceVerificationSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateFaceVerificationSettingsInput) =>
      gpsDrivePlatformIntegrationsService.updateFaceVerificationSettings(input),
    onSuccess: (data) => qc.setQueryData(faceVerificationSettingsKey, data),
  });
}

// WhatsApp da plataforma pros alertas de segurança do servidor (pedido
// 2026-09-24). Polling só enquanto o QR está na tela.
const securityWhatsappKey = ["platform", "integrations", "security-whatsapp"] as const;

export function useSecurityWhatsapp(opts?: { pollWhileConnecting?: boolean }) {
  return useQuery({
    queryKey: securityWhatsappKey,
    queryFn: () => gpsDrivePlatformIntegrationsService.getSecurityWhatsapp(),
    retry: false,
    refetchInterval: opts?.pollWhileConnecting ? 3000 : false,
  });
}

export function useConnectSecurityWhatsapp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => gpsDrivePlatformIntegrationsService.connectSecurityWhatsapp(),
    onSuccess: () => qc.invalidateQueries({ queryKey: securityWhatsappKey }),
  });
}

export function useDisconnectSecurityWhatsapp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => gpsDrivePlatformIntegrationsService.disconnectSecurityWhatsapp(),
    onSuccess: () => qc.invalidateQueries({ queryKey: securityWhatsappKey }),
  });
}

export function useSecurityWhatsappGroups(enabled: boolean) {
  return useQuery({
    queryKey: [...securityWhatsappKey, "groups"],
    queryFn: () => gpsDrivePlatformIntegrationsService.listSecurityWhatsappGroups(false),
    enabled,
    retry: false,
  });
}

export function useSetSecurityWhatsappGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (group: SecurityWhatsappGroup | null) =>
      gpsDrivePlatformIntegrationsService.setSecurityWhatsappGroup(group),
    onSuccess: (data) => qc.setQueryData(securityWhatsappKey, data),
  });
}

export function useTestSecurityWhatsapp() {
  return useMutation({
    mutationFn: () => gpsDrivePlatformIntegrationsService.testSecurityWhatsapp(),
  });
}

// "WhatsApp das empresas" (pedido 2026-09-25) — Super Master vincula uma
// instância uazapi já existente a uma empresa, sem QR code.
const companyWhatsappKey = ["platform", "integrations", "company-whatsapp"] as const;

export function useCompanyWhatsappList() {
  return useQuery({
    queryKey: companyWhatsappKey,
    queryFn: () => gpsDrivePlatformIntegrationsService.listCompanyWhatsapp(),
    retry: false,
  });
}

export function useAttachCompanyExternalInstance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ companyId, token }: { companyId: string; token: string }) =>
      gpsDrivePlatformIntegrationsService.attachCompanyExternalInstance(companyId, token),
    onSuccess: () => qc.invalidateQueries({ queryKey: companyWhatsappKey }),
  });
}

export function useDetachCompanyExternalInstance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (companyId: string) => gpsDrivePlatformIntegrationsService.detachCompanyExternalInstance(companyId),
    onSuccess: () => qc.invalidateQueries({ queryKey: companyWhatsappKey }),
  });
}

// Super Master > Migração (pedido 2026-09-30). Linhas do cliente revalidam
// a cada 10 s pra tela ir mostrando quem sai de offline pra online.
export function useMigrationClients(companyId: string | undefined) {
  return useQuery({
    queryKey: ["platform", "migration", "clients", companyId],
    queryFn: () => gpsDrivePlatformMigrationService.clients(companyId as string),
    enabled: !!companyId,
    retry: false,
  });
}

export function useMigrationContext(companyId: string | undefined) {
  return useQuery({
    queryKey: ["platform", "migration", "context", companyId],
    queryFn: () => gpsDrivePlatformMigrationService.context(companyId as string),
    enabled: !!companyId,
    retry: false,
  });
}

export function useMigrationDevices(companyId: string | undefined, traccarUserId: number | undefined) {
  return useQuery({
    queryKey: ["platform", "migration", "devices", companyId, traccarUserId],
    queryFn: () => gpsDrivePlatformMigrationService.devices(companyId as string, traccarUserId as number),
    enabled: !!companyId && !!traccarUserId,
    refetchInterval: 10_000,
    refetchIntervalInBackground: true,
    retry: 1,
  });
}
